import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useLibraryStore } from "../store/libraryStore";
import { useLegStore } from "../store/legStore";
import { useTripStore } from "../store/tripStore";
import { useUIStore } from "../store/uiStore";
import { legPalette } from "../theme/colors";
import { formatDateRange } from "../utils/format";
import { Button } from "./Button";
import { Modal } from "./Modal";

export function PoiLegPickerModal() {
  const request = useUIStore((state) => state.poiLegPickerRequest);
  const close = useUIStore((state) => state.closePoiLegPicker);
  const trip = useTripStore((state) => state.trip);
  const pinnedPoiIds = useTripStore((state) => state.pinnedPoiIds);
  const schedule = useTripStore((state) => state.schedule);
  const unpinPoi = useTripStore((state) => state.unpinPoi);
  const pois = useLibraryStore((state) => state.pois);
  const legs = useLegStore((state) => state.legs);
  const addPoisToLeg = useLegStore((state) => state.addPoisToLeg);
  const setPoiLegMembership = useLegStore((state) => state.setPoiLegMembership);

  const poi = useMemo(
    () => pois.find((entry) => entry.id === request?.poiId) ?? null,
    [pois, request?.poiId],
  );
  const availableLegs = useMemo(() => {
    if (!request) return [];
    if (request.kind === "manage-pin") return legs;
    const candidates = new Set(request.candidateLegIds);
    return legs.filter((leg) => candidates.has(leg.id));
  }, [legs, request]);
  const scheduledCount = useMemo(
    () =>
      request
        ? schedule.scheduledPOIs.filter((item) => item.poiId === request.poiId)
            .length
        : 0,
    [request, schedule.scheduledPOIs],
  );

  const [selectedLegIds, setSelectedLegIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read through a ref so the seed runs when the modal opens, not every time
  // the legs array changes identity — an optimistic leg update elsewhere would
  // otherwise wipe the selection the user is in the middle of making.
  const legsRef = useRef(legs);
  legsRef.current = legs;

  useEffect(() => {
    if (!request) return;
    setSelectedLegIds(
      request.kind === "manage-pin"
        ? legsRef.current
            .filter((leg) => leg.poiIds.includes(request.poiId))
            .map((leg) => leg.id)
        : [],
    );
    setSaving(false);
    setError(null);
  }, [request]);

  useEffect(() => {
    if (request && availableLegs.length === 0) close();
  }, [availableLegs.length, close, request]);

  if (!request || !trip || !poi) return null;

  const managePin = request.kind === "manage-pin";
  const noLegSelected = selectedLegIds.length === 0;

  const toggleLeg = (legId: string) => {
    setError(null);
    if (managePin) {
      setSelectedLegIds((current) =>
        current.includes(legId)
          ? current.filter((id) => id !== legId)
          : [...current, legId],
      );
    } else {
      setSelectedLegIds((current) => (current[0] === legId ? [] : [legId]));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    if (managePin) {
      const saved = await setPoiLegMembership(trip.id, poi.id, selectedLegIds);
      if (!saved) {
        setSaving(false);
        setError("Leg assignments could not be saved. Try again.");
        return;
      }
    } else if (selectedLegIds[0]) {
      const saved = await addPoisToLeg(trip.id, selectedLegIds[0], [poi.id]);
      if (!saved) {
        setSaving(false);
        setError("The place could not be added to that leg. Try again.");
        return;
      }
    }
    close();
  };

  const handleRemovePin = async () => {
    if (scheduledCount > 0) return;
    setSaving(true);
    setError(null);
    if (await unpinPoi(poi.id)) {
      close();
      return;
    }
    setSaving(false);
    setError("The pin could not be removed. Try again.");
  };

  return (
    <Modal
      open
      onClose={close}
      title={managePin ? `Pin ${poi.name}` : `Choose a leg for ${poi.name}`}
      description={
        managePin
          ? "Choose every leg this place belongs to, or leave it available to the whole trip."
          : "This day belongs to more than one leg. Choose one to add, or keep the place unassigned for this scheduling action."
      }
      icon="keep"
      size="sm"
      footer={
        <>
          <Button onClick={close} variant="secondary" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            variant="primary"
            loading={saving}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-sm leading-relaxed text-secondary">
          {managePin
            ? "Select one or more legs. No leg keeps the place pinned to the trip without assigning it."
            : "Select the leg that best matches this visit."}
        </p>

        <div
          className="flex flex-col gap-2"
          role={managePin ? "group" : "radiogroup"}
          aria-label="Leg assignment"
        >
          <Button
            variant="unstyled"
            role={managePin ? "checkbox" : "radio"}
            aria-checked={noLegSelected}
            data-autofocus={noLegSelected ? "" : undefined}
            onClick={() => {
              setError(null);
              setSelectedLegIds([]);
            }}
            className={clsx(
              "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
              noLegSelected
                ? "border-primary bg-subtle"
                : "border-border bg-surface hover:bg-subtle",
            )}
          >
            <span
              className={clsx(
                "flex size-5 shrink-0 items-center justify-center rounded-full border",
                noLegSelected
                  ? "border-primary bg-primary text-white"
                  : "border-border bg-white text-transparent",
              )}
              aria-hidden
            >
              <span className="material-symbols-outlined text-sm">check</span>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-bold text-primary">
                No leg
              </span>
              <span className="block text-xs text-secondary">
                {managePin
                  ? "Pinned to the trip without a leg"
                  : "Do not add a leg for this scheduling action"}
              </span>
            </span>
          </Button>

          {availableLegs.map((leg) => {
            const selected = selectedLegIds.includes(leg.id);
            const palette = legPalette(leg.color);
            return (
              <Button
                key={leg.id}
                variant="unstyled"
                role={managePin ? "checkbox" : "radio"}
                aria-checked={selected}
                data-autofocus={selectedLegIds[0] === leg.id ? "" : undefined}
                onClick={() => toggleLeg(leg.id)}
                className="flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                style={{
                  borderColor: selected ? palette.border : undefined,
                  backgroundColor: selected ? palette.bg : undefined,
                }}
              >
                <span
                  className="flex size-5 shrink-0 items-center justify-center rounded-full border text-white"
                  style={{
                    borderColor: palette.border,
                    backgroundColor: selected ? palette.dot : "white",
                  }}
                  aria-hidden
                >
                  {selected && (
                    <span className="material-symbols-outlined text-sm">
                      check
                    </span>
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className="block truncate text-sm font-bold"
                    style={{ color: selected ? palette.text : undefined }}
                  >
                    {leg.name}
                  </span>
                  <span className="block text-xs text-secondary">
                    {formatDateRange(leg.startDate, leg.endDate)}
                  </span>
                </span>
              </Button>
            );
          })}
        </div>

        {error && (
          <p
            role="alert"
            className="m-0 rounded-lg bg-error-bg p-3 text-sm text-error"
          >
            {error}
          </p>
        )}

        {managePin && pinnedPoiIds.has(poi.id) && (
          <div className="border-t border-border pt-4">
            <Button
              variant="danger"
              icon="keep_off"
              disabled={scheduledCount > 0 || saving}
              onClick={() => void handleRemovePin()}
            >
              Remove pin
            </Button>
            {scheduledCount > 0 && (
              <p className="mb-0 mt-2 text-xs leading-relaxed text-secondary">
                Remove this place from every scheduled day before removing its
                pin.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
